import { forwardRef } from 'react';
import { MessageAPI } from '@shared/types';
import { cn } from '@web/lib/shadcn';

import { unixTimeToHourAndMinuteStr } from '@shared/utils';

import {
  Check,
  CheckCheck,
  ClockArrowUp,
  Mail,
  MessageSquare,
  MessageCircle,
  MessagesSquare,
  Share2,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@web/components/ui/dialog';

import { File } from '@web/components/ui/file';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@web/components/ui/tooltip';

import { SuggestionsPopover } from './suggestions-popover';
import { CommunicationChannelEnum } from '@shared/enums';

const statusIcon = (status: MessageAPI['status']) => {
  switch (status) {
    case 'queued':
      return <ClockArrowUp size={15} className="text-yellow-500" />;
    case 'sent':
      return <Check size={15} className="text-blue-500" />;
    case 'delivered':
      return <CheckCheck size={15} className="text-green-500" />;
    default:
      return;
  }
};

// Map channel types to their respective icons
const channelIcons: Record<CommunicationChannelEnum, React.ReactNode> = {
  sms: <MessageSquare size={14} />,
  email: <Mail size={14} />,
  whatsapp: <MessagesSquare size={14} />,
  slack: <MessageCircle size={14} />,
  instagram: <Share2 size={14} />,
};

// Map channel types to their respective tooltips
const channelTooltips: Record<CommunicationChannelEnum, string> = {
  sms: 'SMS',
  email: 'Email',
  whatsapp: 'WhatsApp',
  slack: 'Slack',
  instagram: 'Instagram',
};

export const MessageBubble = forwardRef<
  HTMLDivElement,
  {
    message: MessageAPI;
    onSelectReply?: (message: { uuid: string; content: string }) => void;
  }
>(({ message, onSelectReply }, ref) => {
  const content = message.content.split('\n');

  const messageBubbleContent = (
    <div className="w-full p-3 pb-2">
      <div>
        <p className="break-words leading-relaxed text-sm">
          {content.map((line, i) => (
            <span key={i}>
              {line}
              {i < content.length - 1 && <br />}
            </span>
          ))}
        </p>
      </div>

      <div
        ref={ref}
        className="mt-1 flex w-full items-center justify-end gap-1.5"
      >
        <>
          <p
            className={cn(
              'text-[10px] font-medium opacity-70',
              message.direction === 'inbound'
                ? 'text-gray-500'
                : 'text-blue-600',
            )}
          >
            {unixTimeToHourAndMinuteStr(message.timestamp)}
          </p>

          {/* Channel icon with tooltip */}
          {channelIcons[message.channelType] && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      'flex items-center justify-center opacity-60',
                      message.direction === 'inbound'
                        ? 'text-gray-500'
                        : 'text-blue-500',
                    )}
                  >
                    {channelIcons[message.channelType]}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="bg-white text-black shadow-md">
                  <p className="text-xs">{channelTooltips[message.channelType]}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {message.direction === 'outbound' && (
            <span className="opacity-70">{statusIcon(message.status)}</span>
          )}
        </>
      </div>
    </div>
  );

  return (
    <div
      className={cn(
        'relative min-w-[150px] max-w-[85%] overflow-hidden rounded-2xl transition-all',
        message.direction === 'outbound'
          ? 'bg-blue-50 self-end shadow-sm hover:shadow'
          : 'bg-gray-100 text-gray-800 self-start shadow-sm hover:shadow',
      )}
    >
      <div className="flex flex-col gap-1">
        {message.attachments.map(({ fileId }) => {
          const preview = (
            <File
              fileId={fileId}
              className="h-full w-full rounded-t-2xl object-cover"
            />
          );

          return (
            <Dialog key={fileId}>
              <DialogTrigger>
                <div className="max-w-full overflow-hidden">{preview}</div>
              </DialogTrigger>

              <DialogContent className="w-11/12 max-w-none border-0 bg-transparent p-0">
                {preview}
              </DialogContent>
            </Dialog>
          );
        })}
      </div>

      {message.direction === 'inbound' ? (
        <SuggestionsPopover
          message={message}
          onSelectReply={onSelectReply}
          className="w-full"
        >
          {messageBubbleContent}
        </SuggestionsPopover>
      ) : (
        messageBubbleContent
      )}
    </div>
  );
});

MessageBubble.displayName = 'MessageBubble';
